/**
 * rk-cart-upsell — Cart upsell carousel custom element.
 *
 * Carousel mechanics (drag, snap, layout) are handled by rk-carousel
 * (Swiper). This element only takes care of the add-to-cart flow:
 *
 *   – Click on a card's "Adicionar" button (data-action="add"):
 *       • single-variant product → POST /cart/add.js, dispatch cart:update,
 *         and remove the slide for immediate feedback.
 *       • multi-variant product → open the global quick-add dialog with
 *         the product's grid content.
 *
 * Mirrors the same delegation pattern used in rk-cart-gift.js: all the
 * data needed for the action lives on the button itself, so the handler
 * never has to traverse to a parent card.
 */
class RkCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (/** @type {MouseEvent} */ e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    const addBtn = /** @type {HTMLButtonElement | null} */ (
      target.closest('[data-action="add"]')
    );
    if (!addBtn || addBtn.disabled) return;

    const variantsCount = Number(addBtn.dataset['variantsCount'] || '1');

    if (variantsCount > 1) {
      this.#openQuickAdd(addBtn);
      return;
    }

    this.#addToCart(addBtn);
  };

  /** @param {HTMLButtonElement} btn */
  #addToCart = async (btn) => {
    const variantId = Number(btn.dataset['variantId']);
    if (!variantId) return;

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
        }),
      });

      if (!response.ok) {
        throw new Error(`Add to cart failed: ${response.status}`);
      }

      this.#dispatchCartUpdate();

      /* Immediate visual feedback: drop the slide. The cart re-render
         driven by cart:update is the source of truth and will keep
         already-in-cart products hidden on the next paint. */
      const slide = btn.closest('.swiper-slide');
      if (slide) slide.remove();
    } catch (err) {
      console.error('[rk-cart-upsell]', err);
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  };

  #dispatchCartUpdate() {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { source: 'rk-cart-upsell' } },
      })
    );
  }

  /** @param {HTMLButtonElement} btn */
  #openQuickAdd = async (btn) => {
    const productUrl = btn.dataset['productUrl'];
    if (!productUrl) return;

    const dialog = /** @type {HTMLElement & { showDialog?: () => void }} */ (
      document.getElementById('quick-add-dialog')
    );
    if (!dialog) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    try {
      const response = await fetch(productUrl);
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );
      const productGrid = html.querySelector('[data-product-grid-content]');
      if (!productGrid) return;

      /* Mobile reorganization mirrors quick-add.js → updateQuickAddModal()
         (assets/quick-add.js:206-234). On viewports below 750px the
         shared <quick-add-dialog> is rendered as a bottom sheet and
         expects a drawer-shaped layout: header + variant picker + form
         only, with product-details stripped out. Without this, the
         desktop product grid layout overflows the mobile drawer. */
      const isMobile = !window.matchMedia('(min-width: 750px)').matches;

      if (isMobile) {
        const productDetails = productGrid.querySelector('.product-details');
        const productFormComponent = productGrid.querySelector(
          'product-form-component'
        );
        const variantPicker = productGrid.querySelector('variant-picker');
        const productPrice = productGrid.querySelector('product-price');

        const productTitle = document.createElement('a');
        productTitle.textContent = btn.dataset['productTitle'] || '';
        productTitle.href = productUrl;

        const productHeader = document.createElement('div');
        productHeader.classList.add('product-header');
        productHeader.appendChild(productTitle);
        if (productPrice) productHeader.appendChild(productPrice);

        productGrid.appendChild(productHeader);
        if (variantPicker) productGrid.appendChild(variantPicker);
        if (productFormComponent) productGrid.appendChild(productFormComponent);

        productDetails?.remove();
      }

      modalContent.innerHTML = productGrid.innerHTML;
      dialog.showDialog?.();
    } catch (err) {
      console.error('[rk-cart-upsell] Quick add fetch failed:', err);
    }
  };
}

if (!customElements.get('rk-cart-upsell')) {
  customElements.define('rk-cart-upsell', RkCartUpsell);
}
